import { createContext, useContext, useState, useCallback, useRef, useMemo, ReactNode } from 'react'

interface PendingAction {
  type: 'seek' | 'playRange'
  start: number
  end?: number
}

interface AudioMix {
  mutedTracks: Record<string, boolean>
  trackVolumes: Record<string, number>
  clipMutes: Record<string, boolean>
  clipVolumes: Record<string, number>
}

export interface BufferDebugData {
  /** Time ranges where video blobs are fully prefetched (in memory) */
  prefetched: Array<{ start: number; end: number }>
  /** Time ranges where prefetch is in-flight */
  pending: Array<{ start: number; end: number }>
  /** Clips with resolved blob URLs */
  readyCount: number
  /** Total video clips in prefetch window */
  totalCount: number
}

interface VideoSeekContextValue {
  seekTo: (seconds: number) => void
  playRange: (startSeconds: number, endSeconds: number) => void
  togglePlayPause: () => void
  scrubTo: (seconds: number) => void
  scrubStart: () => void
  scrubEnd: () => void
  registerSeekHandler: (handler: (seconds: number) => void) => void
  registerPlayRangeHandler: (handler: (start: number, end: number) => void) => void
  registerSwitchToPreview: (handler: () => void) => void
  registerPlayPauseHandler: (handler: () => void) => void
  registerScrubHandler: (handler: (seconds: number) => void) => void
  registerScrubStartHandler: (handler: () => void) => void
  registerScrubEndHandler: (handler: () => void) => void
  highlightedRange: { start: number; end: number } | null
  highlightedPoint: number | null
  pendingAction: PendingAction | null
  clearPendingAction: () => void
  currentTime: number
  currentTimeRef: { readonly current: number }
  updateCurrentTime: (time: number) => void
  isPlaying: boolean
  updateIsPlaying: (playing: boolean) => void
  audioMix: AudioMix
  setTrackMuted: (trackId: string, muted: boolean) => void
  setTrackVolume: (trackId: string, volume: number) => void
  setClipMuted: (clipId: string, muted: boolean) => void
  setClipVolume: (clipId: string, volume: number) => void
  bufferDebugRef: { readonly current: BufferDebugData | null }
  updateBufferDebug: (data: BufferDebugData) => void
}

const VideoSeekContext = createContext<VideoSeekContextValue | null>(null)

export function VideoSeekProvider({ children }: { children: ReactNode }) {
  const seekHandlerRef = useRef<((seconds: number) => void) | null>(null)
  const playRangeHandlerRef = useRef<((start: number, end: number) => void) | null>(null)
  const switchToPreviewHandlerRef = useRef<(() => void) | null>(null)
  const playPauseHandlerRef = useRef<(() => void) | null>(null)
  const scrubHandlerRef = useRef<((seconds: number) => void) | null>(null)
  const scrubStartHandlerRef = useRef<(() => void) | null>(null)
  const scrubEndHandlerRef = useRef<(() => void) | null>(null)
  const [highlightedRange, setHighlightedRange] = useState<{ start: number; end: number } | null>(null)
  const [highlightedPoint, setHighlightedPoint] = useState<number | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const currentTimeRef = useRef(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const [audioMix, setAudioMix] = useState<AudioMix>({
    mutedTracks: {},
    trackVolumes: {},
    clipMutes: {},
    clipVolumes: {},
  })
  const bufferDebugRef = useRef<BufferDebugData | null>(null)
  const updateBufferDebug = useCallback((data: BufferDebugData) => {
    bufferDebugRef.current = data
  }, [])

  const isPlayingRef = useRef(false)
  const lastStateUpdateRef = useRef(0)
  const updateCurrentTime = useCallback((time: number) => {
    currentTimeRef.current = time
    if (!isPlayingRef.current) {
      setCurrentTime(time)
      return
    }
    const now = performance.now()
    if (now - lastStateUpdateRef.current > 250) {
      lastStateUpdateRef.current = now
      setCurrentTime(time)
    }
  }, [])

  const updateIsPlaying = useCallback((playing: boolean) => {
    isPlayingRef.current = playing
    setIsPlaying(playing)
    if (!playing) {
      setCurrentTime(currentTimeRef.current)
    }
  }, [])

  const registerSeekHandler = useCallback((handler: (seconds: number) => void) => {
    seekHandlerRef.current = handler
  }, [])

  const registerPlayRangeHandler = useCallback((handler: (start: number, end: number) => void) => {
    playRangeHandlerRef.current = handler
  }, [])

  const registerSwitchToPreview = useCallback((handler: () => void) => {
    switchToPreviewHandlerRef.current = handler
  }, [])

  const registerPlayPauseHandler = useCallback((handler: () => void) => {
    playPauseHandlerRef.current = handler
  }, [])

  const registerScrubHandler = useCallback((handler: (seconds: number) => void) => {
    scrubHandlerRef.current = handler
  }, [])

  const registerScrubStartHandler = useCallback((handler: () => void) => {
    scrubStartHandlerRef.current = handler
  }, [])

  const registerScrubEndHandler = useCallback((handler: () => void) => {
    scrubEndHandlerRef.current = handler
  }, [])

  const togglePlayPause = useCallback(() => {
    playPauseHandlerRef.current?.()
  }, [])

  const scrubTo = useCallback((seconds: number) => {
    currentTimeRef.current = seconds
    setCurrentTime(seconds)
    scrubHandlerRef.current?.(seconds)
  }, [])

  const scrubStart = useCallback(() => {
    scrubStartHandlerRef.current?.()
  }, [])

  const scrubEnd = useCallback(() => {
    scrubEndHandlerRef.current?.()
  }, [])

  const clearPendingAction = useCallback(() => {
    setPendingAction(null)
  }, [])

  const seekTo = useCallback((seconds: number) => {
    setHighlightedRange(null)
    setHighlightedPoint(seconds)
    currentTimeRef.current = seconds
    setCurrentTime(seconds)
    switchToPreviewHandlerRef.current?.()
    if (seekHandlerRef.current) {
      seekHandlerRef.current(seconds)
    } else {
      setPendingAction({ type: 'seek', start: seconds })
    }
    setTimeout(() => setHighlightedPoint(null), 3000)
  }, [])

  const playRange = useCallback((startSeconds: number, endSeconds: number) => {
    setHighlightedPoint(null)
    setHighlightedRange({ start: startSeconds, end: endSeconds })
    switchToPreviewHandlerRef.current?.()
    if (playRangeHandlerRef.current) {
      playRangeHandlerRef.current(startSeconds, endSeconds)
    } else {
      setPendingAction({ type: 'playRange', start: startSeconds, end: endSeconds })
    }
    setTimeout(() => setHighlightedRange(null), (endSeconds - startSeconds + 1) * 1000)
  }, [])

  const setTrackMuted = useCallback((trackId: string, muted: boolean) => {
    setAudioMix(prev => ({ ...prev, mutedTracks: { ...prev.mutedTracks, [trackId]: muted } }))
  }, [])

  const setTrackVolume = useCallback((trackId: string, volume: number) => {
    setAudioMix(prev => ({ ...prev, trackVolumes: { ...prev.trackVolumes, [trackId]: volume } }))
  }, [])

  const setClipMuted = useCallback((clipId: string, muted: boolean) => {
    setAudioMix(prev => ({ ...prev, clipMutes: { ...prev.clipMutes, [clipId]: muted } }))
  }, [])

  const setClipVolume = useCallback((clipId: string, volume: number) => {
    setAudioMix(prev => ({ ...prev, clipVolumes: { ...prev.clipVolumes, [clipId]: volume } }))
  }, [])

  const value = useMemo(() => ({
    seekTo,
    playRange,
    togglePlayPause,
    scrubTo,
    scrubStart,
    scrubEnd,
    registerSeekHandler,
    registerPlayRangeHandler,
    registerSwitchToPreview,
    registerPlayPauseHandler,
    registerScrubHandler,
    registerScrubStartHandler,
    registerScrubEndHandler,
    highlightedRange,
    highlightedPoint,
    pendingAction,
    clearPendingAction,
    currentTime,
    currentTimeRef,
    updateCurrentTime,
    isPlaying,
    updateIsPlaying,
    audioMix,
    setTrackMuted,
    setTrackVolume,
    setClipMuted,
    setClipVolume,
    bufferDebugRef,
    updateBufferDebug,
  }), [seekTo, playRange, togglePlayPause, scrubTo, scrubStart, scrubEnd, registerSeekHandler, registerPlayRangeHandler, registerSwitchToPreview, registerPlayPauseHandler, registerScrubHandler, registerScrubStartHandler, registerScrubEndHandler, highlightedRange, highlightedPoint, pendingAction, clearPendingAction, currentTime, currentTimeRef, updateCurrentTime, isPlaying, updateIsPlaying, audioMix, setTrackMuted, setTrackVolume, setClipMuted, setClipVolume, updateBufferDebug])

  return (
    <VideoSeekContext.Provider value={value}>
      {children}
    </VideoSeekContext.Provider>
  )
}

export function useVideoSeek() {
  const context = useContext(VideoSeekContext)
  if (!context) {
    throw new Error('useVideoSeek must be used within VideoSeekProvider')
  }
  return context
}

export function useVideoSeekOptional() {
  return useContext(VideoSeekContext)
}
